import _ from "lodash";

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import DriveShareModal from "UIComponents/DriveShareModal/DriveShareModal";
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";
import DrivePermissionModal from "UIComponents/GooglePicker/DrivePermissionModal";

import {
  generateRandomId,
  getFileName,
  getGoogleFileDetails,
  getMicrosoftFileDetails,
  getMediaCardFileType,
} from "Utils";
import { checkUserDriveAccess } from "Utils/AttachmentHelpers";
import { getSelectedLearningCourse } from "LearningCourse/modules/Utils";

import {
  getSelectedCurriculumId,
  getCourseId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import { createNewThirdPartyDocument } from "./modules/CreateDriveDocumentGraphqlHelpers";

import NameModal from "./NameModal";

const CreateDriveDocument = ({
  t,
  closeDriveDocument,
  onDropContent,
  onSingleContentUploaded,
  onMultipleFilesUploaded,
  updateFilesCount,
  academicYearId,
  curriculumProgramId,
  learningCourseId,
  classId,
  submissionId,
  parentId,
  parentType,
  sourceType,
  serviceType,
  type,
  documentType,
  defaultExportType,
  attachmentExportOptions,
  createNewThirdPartyDocument,
  setToastMsg,
}) => {
  const [showNameModal, setShowNameModal] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDrivePermissionModal, setShowDrivePermissionModal] = useState(
    false
  );
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const authPopUpRef = useRef(null);

  useEffect(() => {
    onAuthClick();
  }, []);

  const handleAuthSuccess = async ({ accessToken, id }) => {
    if (_.isEqual(serviceType, "GOOGLE")) {
      const hasSufficientDriveAccess = await checkUserDriveAccess({
        accessToken,
      });
      if (!hasSufficientDriveAccess) {
        setShowDrivePermissionModal(true);
        setAccountId(id);
        return;
      }
    }
    setShowNameModal(true);
    setAccessToken(accessToken);
  };

  const onAuthClick = () => {
    const authInstance = _.get(authPopUpRef, "current.refs.wrappedInstance");
    if (authInstance?.onAuthClick) {
      authInstance.onAuthClick();
    }
  };

  const closeDrivePermissionModal = () => {
    setShowDrivePermissionModal(false);
    setAccountId("");
    closeDriveDocument();
  };

  const onCreate = async ({ name }) => {
    setIsLoading(true);
    const response = await createNewThirdPartyDocument({
      name,
      serviceType,
      type,
      documentType,
      academicYearId,
      curriculumProgramId,
      learningCourseId,
      classId,
      submissionId,
    });

    const { isSuccess = false, data, warning = "" } = response || {};
    if (!isSuccess) {
      setToastMsg(warning || "toastMsgs:something_went_wrong");
      setIsLoading(false);
      return;
    }
    setShowNameModal(false);
    const fileData =
      serviceType === "GOOGLE"
        ? {
            id: data.id,
            url: data.webViewLink,
            mimeType: data.mimeType,
            name,
          }
        : {
            ...data,
            name,
            url: data.webUrl,
            type: getMediaCardFileType({
              type: "FILE",
              mimeType: _.get(data, "file.mimeType", ""),
            }),
            mimeType: _.get(data, "file.mimeType", ""),
            size: data.size,
            id: data.id,
          };

    setFile(fileData);
    if (_.isEqual(sourceType, "STUDENT_TEMPLATE")) {
      setShowShareModal(true);
    } else {
      await processFile({ files: [fileData] });
      closeDriveDocument();
    }
    setIsLoading(false);
  };

  const getGoogleSendObject = ({ file }) => {
    const mimeType = file.mimeType;
    const url = file.url;
    const dimensions = { height: 520, width: 520 };
    const metadata = {
      size: _.get(file, "size", 0),
      ...dimensions,
      serviceType,
      fileType: "DOCUMENT",
      isNewDriveDocument: true,
    };
    if (file.permission) {
      metadata.permission = file.permission;
    }
    return {
      type: "LINK",
      url: url,
      name: getFileName(file.name, mimeType),
      mimeType: mimeType,
      metadata,
    };
  };

  const getMicrosoftSendObject = ({ file }) => {
    const mimeType = _.get(file, "file.mimeType")
      ? file.mimeType
      : _.get(file, "type");
    const url = file.url;

    const metadata = {
      size: _.get(file, "size", 0),
      serviceType: "MICROSOFT",
      fileType:
        _.get(file, "type") ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ? "document"
          : _.get(file, "type"),
      fileId: _.get(file, "id"),
      isNewDriveDocument: true,
    };

    if (file.permission) {
      metadata.permission = file.permission;
    }
    return {
      type: "LINK",
      url: url,
      name: getFileName(file.name),
      mimeType: mimeType,
      metadata,
    };
  };

  const getSendObject = ({ file, item }) => {
    switch (serviceType) {
      case "GOOGLE":
        return getGoogleSendObject({ file, item });
      case "MICROSOFT":
        return getMicrosoftSendObject({ file });
      default:
        return null;
    }
  };

  const onFileUpload = ({ sendObj, fileUploadId }) => {
    if (onDropContent) {
      onDropContent({ ...sendObj, id: fileUploadId });
    }
    if (onSingleContentUploaded) {
      onSingleContentUploaded(
        sendObj,
        fileUploadId,
        {
          parentId,
          parentType,
        },
        true
      );
    }
    if (onMultipleFilesUploaded) {
      onMultipleFilesUploaded([sendObj]);
    }
    updateFilesCount(-1, false, _.isEqual(type, "SUBMISSION_DOCUMENT"));
  };

  const processGoogleFile = async ({ files }) => {
    const file = files[0];
    updateFilesCount(1, false, _.isEqual(type, "SUBMISSION_DOCUMENT"));
    const fileUploadId = generateRandomId();
    const fileDetails = await getGoogleFileDetails({
      fileId: file.id,
      accessToken,
    });
    const thumbUrl = _.get(fileDetails, "data.thumbnailLink");
    const largerThumbnailUrl = thumbUrl?.replace("s220", "s2048");

    const sendObj = getSendObject({
      file,
      item: thumbUrl,
    });

    //Sending this metadata to for BE to download and upload to S3.
    //Sending this with object as thumbUrl with to show preview while creation.
    if (largerThumbnailUrl) {
      sendObj.metadata.tempThumbUrl = largerThumbnailUrl;
      sendObj.thumbUrl = largerThumbnailUrl;
    }

    onFileUpload({ sendObj, fileUploadId });
  };

  const processMicrosoftFile = async ({ files }) => {
    const file = files[0];
    updateFilesCount(1, false, _.isEqual(type, "SUBMISSION_DOCUMENT"));

    const fileUploadId = generateRandomId();
    const fileDetails = await getMicrosoftFileDetails({
      fileId: file.id,
      accessToken,
    });

    const thumbUrl = _.get(fileDetails, "thumbnailLink");

    const sendObj = getSendObject({
      file,
    });

    //Sending this metadata to for BE to download and upload to S3.
    //Sending this with object as thumbUrl with to show preview while creation.
    if (thumbUrl) {
      sendObj.metadata.tempThumbUrl = thumbUrl;
      sendObj.thumbUrl = thumbUrl;
    }
    onFileUpload({ sendObj, fileUploadId });
  };

  const processFile = async ({ files }) => {
    switch (serviceType) {
      case "GOOGLE":
        await processGoogleFile({ files });
        break;
      case "MICROSOFT":
        await processMicrosoftFile({ files });
        break;
      default:
        break;
    }
  };

  const handleStopPropagation = e => {
    e?.stopPropagation?.();
  };

  return (
    <>
      <ThirdPartyAuth
        serviceType={serviceType}
        ref={authPopUpRef}
        onSuccessCallBack={handleAuthSuccess}
        onFailureCallBack={closeDriveDocument}
      />
      {isLoading && <FullScreenLoader />}
      {showNameModal && (
        <NameModal
          t={t}
          closeModal={closeDriveDocument}
          onDocumentNameSubmit={onCreate}
          documentType={documentType}
          serviceType={serviceType}
        />
      )}
      {showShareModal && (
        <div onClick={handleStopPropagation}>
          <DriveShareModal
            serviceType={serviceType}
            processChosenFiles={processFile}
            onClickClose={closeDriveDocument}
            files={[file]}
            accessToken={accessToken}
            sourceType={"STUDENT_TEMPLATE"}
            defaultExportType={defaultExportType}
            attachmentExportOptions={attachmentExportOptions}
            userType={"staff"}
            customStyle={{ zIndex: 1151 }}
          />
        </div>
      )}
      {showDrivePermissionModal && !_.isEmpty(accountId) && (
        <div onClick={handleStopPropagation}>
          <DrivePermissionModal
            accountId={accountId}
            onAuthClick={onAuthClick}
            closeModal={closeDrivePermissionModal}
          />
        </div>
      )}
    </>
  );
};

const mapActionCreators = {
  createNewThirdPartyDocument,
  setToastMsg,
};

const mapStateToProps = (state, props) => {
  let curriculumProgramId = _.get(props, "curriculumProgramId");
  let learningCourseId = _.get(props, "learningCourseId", "");
  let courseId = _.get(props, "courseId", "");
  const academicYearId = getSelectedAcademicYearId(state);
  if (_.isEmpty(curriculumProgramId)) {
    curriculumProgramId = getSelectedCurriculumId(state);
  }
  if (_.isEmpty(learningCourseId)) {
    learningCourseId = _.get(getSelectedLearningCourse(state), "id", "");
  }
  if (_.isEmpty(courseId)) {
    courseId = getCourseId(state);
  }
  return {
    academicYearId,
    curriculumProgramId,
    learningCourseId,
    classId: courseId,
  };
};
export default connect(mapStateToProps, mapActionCreators)(CreateDriveDocument);
